import type { IconName as SkiliticsIconName } from "@awesome.me/kit-c7777e19bb/icons";
import type { IconName as FaIconName, IconPrefix } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { ComponentProps } from "react";

import type { SkiliticsDuotoneIconName } from "./customIconDefinitions";

export type IconName = FaIconName;
export { type SkiliticsIconName };

export interface IconProps {
  className?: string;
  icon: IconName | SkiliticsIconName | SkiliticsDuotoneIconName | IconName[];
  onClick?: React.MouseEventHandler;
  faPrefix?: IconPrefix | "fakd";
  size?: number;
  /** Per-layer font sizes, applied by index. Only used when `icon` is an array. */
  layerSizes?: number[];
  rotation?: ComponentProps<typeof FontAwesomeIcon>["rotation"];
  flip?: ComponentProps<typeof FontAwesomeIcon>["flip"];
  spin?: ComponentProps<typeof FontAwesomeIcon>["spin"];
}

export const Icon = ({ className, icon, onClick, faPrefix, size, layerSizes, flip, rotation, ...rest }: IconProps) => {
  const prefix = (faPrefix as IconPrefix) || "far";
  const cssClass = `fa-icon-wrapper ${className ?? ""}`;

  if (Array.isArray(icon)) {
    return (
      <i className={"fa-layers fa-fw " + cssClass} onClick={onClick} style={{ fontSize: size ?? undefined }}>
        {icon.map((i, index) => (
          <FontAwesomeIcon
            key={index}
            className="fa-icon"
            icon={{ prefix, iconName: i }}
            style={layerSizes?.[index] ? { fontSize: layerSizes[index] } : undefined}
            {...rest}
          />
        ))}
      </i>
    );
  }
  return (
    <i className={cssClass} onClick={onClick} style={{ fontSize: size ?? undefined }}>
      <FontAwesomeIcon
        className="fa-icon"
        icon={{ prefix, iconName: icon as IconName }}
        style={{ transform: combineCssTransforms(rotation, flip) }}
        {...rest}
      />
    </i>
  );
};

export default Icon;

type SkiliticsIconProps = Omit<IconProps, "icon"> & {
  icon: SkiliticsIconName;
};

export const SkiliticsIcon = (props: SkiliticsIconProps) => <Icon faPrefix="fak" {...props} />;

type SkiliticsDuotoneIconProps = Omit<IconProps, "icon"> & {
  icon: SkiliticsDuotoneIconName;
};

export const SkiliticsDuotoneIcon = (props: SkiliticsDuotoneIconProps) => <Icon faPrefix="fad" {...props} />;

const combineCssTransforms = (rotation: IconProps["rotation"], flip: IconProps["flip"]) => {
  const flipTransform =
    flip === "horizontal"
      ? "scaleX(-1)"
      : flip === "vertical"
        ? "scaleY(-1)"
        : flip === "both"
          ? "scale(-1)"
          : undefined;

  const rotateTransform = rotation ? `rotate(${rotation}deg)` : undefined;

  return [flipTransform, rotateTransform].filter((t) => !!t).join(" ");
};
